import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, IconButton, Chip, Rating } from '@mui/material';
import { Close as CloseIcon, Star as StarIcon, Edit as EditIcon, Clear as ClearIcon } from '@mui/icons-material';

const ScoreCardDrawer = ({ scoreCardData, onClose, onSave, mode = 'edit' }) => {
  const [isEditMode, setIsEditMode] = useState(mode === 'edit');
  const [formData, setFormData] = useState({
    productMarketFit: null,
    executionBottleneck: null,
    gatewayOperatorLeverage: null,
    founderAlignment: null,
    capitalEfficiency: null,
    notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [originalData, setOriginalData] = useState(null);
  
  // Initialize form data only once when drawer opens
  useEffect(() => {
    if (scoreCardData && !initialized) {
      const data = {
        productMarketFit: scoreCardData.productMarketFit || null,
        executionBottleneck: scoreCardData.executionBottleneck || null,
        gatewayOperatorLeverage: scoreCardData.gatewayOperatorLeverage || null,
        founderAlignment: scoreCardData.founderAlignment || null,
        capitalEfficiency: scoreCardData.capitalEfficiency || null,
        notes: scoreCardData.notes || ''
      };
      setFormData(data);
      setOriginalData(data);
      setInitialized(true);
    }
  }, [scoreCardData, initialized]);
  
  // Reset initialization flag when drawer closes
  useEffect(() => {
    return () => {
      setInitialized(false);
    };
  }, []);
  
  const calculateOverallScore = () => {
    const scores = [
      formData.productMarketFit || 0,
      formData.executionBottleneck || 0,
      formData.gatewayOperatorLeverage || 0,
      formData.founderAlignment || 0,
      formData.capitalEfficiency || 0
    ];
    return scores.reduce((sum, score) => sum + score, 0);
  };
  
  const getOverallColor = (score) => {
    if (score < 18) return { color: '#d32f2f', label: 'Red', bg: '#ffebee' };
    if (score <= 21) return { color: '#f57c00', label: 'Orange', bg: '#fff3e0' };
    return { color: '#388e3c', label: 'Green', bg: '#e8f5e9' };
  };
  
  const hasRedFlag = (dimension, value) => {
    if (!value) return false;
    if (dimension === 'executionBottleneck' && value < 4) return true;
    if (dimension === 'gatewayOperatorLeverage' && value < 3) return true;
    if (dimension === 'founderAlignment' && value < 3) return true;
    return false;
  };
  
  const handleSave = async () => {
    setLoading(true);
    try {
      const dataToSave = {
        product_market_fit: formData.productMarketFit,
        execution_bottleneck: formData.executionBottleneck,
        gateway_operator_leverage: formData.gatewayOperatorLeverage,
        founder_alignment: formData.founderAlignment,
        capital_efficiency: formData.capitalEfficiency,
        notes: formData.notes
      };
      await onSave(dataToSave);
      onClose();
    } catch (error) {
      console.error('Error in handleSave:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClearRatings = () => {
    setFormData({
      productMarketFit: null,
      executionBottleneck: null,
      gatewayOperatorLeverage: null,
      founderAlignment: null,
      capitalEfficiency: null,
      notes: formData.notes
    });
  };

  const handleCancel = () => {
    if (originalData) {
      setFormData(originalData);
    }
    setIsEditMode(false);
  };

  const handleEdit = () => {
    setIsEditMode(true);
  };

  const overallScore = calculateOverallScore();
  const overallColorData = getOverallColor(overallScore);

  const dimensions = [
    { 
      key: 'productMarketFit', 
      label: 'Product-Market Fit', 
      question: 'Is Demand Proven',
      icon: '📊'
    },
    { 
      key: 'executionBottleneck', 
      label: 'Execution Bottleneck', 
      question: 'Is execution of hyper-scaling the main limiter?',
      icon: '⚡'
    },
    { 
      key: 'gatewayOperatorLeverage', 
      label: 'Gateway/Operator Leverage', 
      question: 'Can operators materially change outcomes?',
      icon: '🎯'
    },
    { 
      key: 'founderAlignment', 
      label: 'Founder Alignment', 
      question: 'Founder/Board ready for structure?',
      icon: '🤝'
    },
    { 
      key: 'capitalEfficiency', 
      label: 'Capital Efficiency', 
      question: 'Can we reduce cost & burn 30-40%?',
      icon: '💰'
    }
  ];

  const RatingRow = ({ dimension, value, onChange, readOnly }) => {
    const flag = hasRedFlag(dimension.key, value);
    
    return (
      <Box sx={{ 
        mb: 1, 
        p: 1.2, 
        backgroundColor: flag ? '#ffebee' : '#f8f9fa', 
        borderRadius: 1.2,
        border: flag ? '1px solid #ef5350' : '1px solid #e9ecef',
        transition: 'all 0.2s'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, flex: 1 }}>
            <Typography sx={{ fontSize: '0.95rem' }}>{dimension.icon}</Typography>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#212529', lineHeight: 1.2 }}>
                {dimension.label}
              </Typography>
              <Typography variant="caption" sx={{ color: '#6c757d', fontSize: '0.65rem', lineHeight: 1.2 }}>
                {dimension.question}
              </Typography>
            </Box>
          </Box>
          {flag && (
            <Chip 
              label="⚠️" 
              size="small" 
              sx={{ 
                height: 18,
                fontSize: '0.65rem',
                backgroundColor: '#d32f2f', 
                color: '#fff',
                minWidth: 26
              }} 
            />
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, mt: 0.6 }}>
          <Rating
            value={value || 0}
            max={5}
            onChange={(event, newValue) => !readOnly && onChange(newValue)}
            readOnly={readOnly}
            size="small"
            icon={<StarIcon sx={{ fontSize: '1.2rem' }} />}
            emptyIcon={<StarIcon sx={{ fontSize: '1.2rem', color: '#e0e0e0' }} />}
            sx={{
              '& .MuiRating-iconFilled': {
                color: flag ? '#d32f2f' : '#ffc107'
              }
            }}
          />
          <Typography variant="body2" sx={{ 
            fontWeight: 700, 
            color: flag ? '#d32f2f' : '#1976d2',
            minWidth: 32,
            fontSize: '0.85rem'
          }}>
            {value || 0}/5
          </Typography>
        </Box>
      </Box>
    );
  };

  // Common UI for both view and edit modes
  return (
    <Box sx={{ width: 500, height: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0', backgroundColor: '#fafafa' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
            Score Card
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {!isEditMode && (
              <IconButton onClick={handleEdit} size="small" sx={{ color: '#1976d2' }}>
                <EditIcon fontSize="small" />
              </IconButton>
            )}
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
        <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
          Company evaluation metrics
        </Typography>
      </Box>
      
      <Box sx={{ 
        flex: 1, 
        p: 2, 
        overflowY: 'auto',
        '&::-webkit-scrollbar': { width: '6px' },
        '&::-webkit-scrollbar-track': { background: '#f1f1f1', borderRadius: '10px' },
        '&::-webkit-scrollbar-thumb': { background: '#c1c1c1', borderRadius: '10px' }
      }}>
        {/* Overall Score - Compact */}
        <Box sx={{ 
          mb: 1.5, 
          p: 1.5, 
          backgroundColor: overallColorData.bg, 
          borderRadius: 1.5, 
          border: `2px solid ${overallColorData.color}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Box>
            <Typography variant="caption" sx={{ color: '#666', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.65rem', display: 'block', mb: 0.3 }}>
              Overall Score (Auto)
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 700, color: overallColorData.color, fontSize: '1.8rem', lineHeight: 1 }}>
              {overallScore}/25
            </Typography>
          </Box>
          <Chip 
            label={overallColorData.label} 
            sx={{ 
              backgroundColor: overallColorData.color, 
              color: '#fff', 
              fontWeight: 600,
              fontSize: '0.7rem',
              height: 22,
              px: 1
            }} 
          />
        </Box>
        
        {/* Individual Dimensions with Rating Bars */}
        <Box sx={{ mb: 1.5 }}>
          {dimensions.map((dim) => (
            <RatingRow
              key={dim.key}
              dimension={dim}
              value={formData[dim.key]}
              onChange={(newValue) => setFormData(prev => ({ ...prev, [dim.key]: newValue }))}
              readOnly={!isEditMode}
            />
          ))}
        </Box>
        
        {/* Clear Ratings Button - Only in Edit Mode */}
        {isEditMode && (
          <Box sx={{ mb: 1.5, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<ClearIcon />}
              onClick={handleClearRatings}
              sx={{
                borderColor: '#d32f2f',
                color: '#d32f2f',
                fontSize: '0.75rem',
                '&:hover': {
                  borderColor: '#b71c1c',
                  backgroundColor: '#ffebee'
                }
              }}
            >
              Clear Ratings
            </Button>
          </Box>
        )}
        
        {/* Notes */}
        <Box sx={{ mb: 1 }}>
          <Typography variant="caption" sx={{ color: '#6c757d', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', mb: 0.8, fontSize: '0.65rem' }}>
            Overall Notes
          </Typography>
          {isEditMode ? (
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              placeholder="Add notes..."
              style={{
                width: '100%',
                minHeight: '60px',
                padding: '10px',
                borderRadius: '6px',
                border: '1px solid #e0e0e0',
                fontFamily: 'inherit',
                fontSize: '13px',
                resize: 'vertical'
              }}
            />
          ) : (
            formData.notes ? (
              <Box sx={{ p: 1.2, backgroundColor: '#f8f9fa', borderRadius: 1.2, border: '1px solid #e9ecef' }}>
                <Typography variant="body2" sx={{ color: '#212529', whiteSpace: 'pre-wrap', fontSize: '0.8rem', lineHeight: 1.4 }}>
                  {formData.notes}
                </Typography>
              </Box>
            ) : (
              <Typography variant="body2" sx={{ color: '#9e9e9e', fontStyle: 'italic', fontSize: '0.8rem' }}>
                No notes added
              </Typography>
            )
          )}
        </Box>
      </Box>
      
      <Box sx={{ p: 2, borderTop: '1px solid #e0e0e0' }}>
        {isEditMode ? (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button 
              variant="outlined" 
              fullWidth 
              onClick={handleCancel}
              sx={{ 
                py: 1.2, 
                fontSize: '0.9rem', 
                fontWeight: 600,
                borderColor: '#e0e0e0',
                color: 'text.secondary'
              }}
            >
              Cancel
            </Button>
            <Button 
              variant="contained" 
              fullWidth 
              onClick={handleSave}
              disabled={loading}
              sx={{ 
                backgroundColor: '#4caf50', 
                '&:hover': { backgroundColor: '#45a049' }, 
                py: 1.2, 
                fontSize: '0.9rem', 
                fontWeight: 600 
              }}
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </Button>
          </Box>
        ) : (
          <Button 
            variant="outlined" 
            fullWidth 
            onClick={onClose}
            sx={{ 
              py: 1.2, 
              fontSize: '0.9rem', 
              fontWeight: 600,
              borderColor: '#e0e0e0',
              color: 'text.secondary'
            }}
          >
            Close
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default ScoreCardDrawer;
